import { useState, useEffect } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@component/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { CameraIcon, Loader2Icon, MicIcon, SpeakerIcon } from "lucide-react";

interface DeviceOption {
    deviceId: string;
    label: string;
    fullLabel?: string; // Store the full untruncated label
}

const truncateLabel = (label: string, maxLength: number = 40): string => {
    return label.length > maxLength ? `${label.substring(0, maxLength)}...` : label;
};

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (devices: { camera?: string; microphone?: string; speaker?: string }) => void; // New prop
    initialSelectedCamera?: string;     // New prop
    initialSelectedMicrophone?: string; // New prop
    initialSelectedSpeaker?: string;    // New prop
    colorScheme?: 'default' | 'black'; // Color scheme for different interview types
    /** When true, hides the Camera row and only requests audio permission (voice-only flows). */
    hideCamera?: boolean;
    /**
     * Ticket #1870, PRD §3.2: when the in-interview audio detection reports
     * weak or no audio, surface a small warning indicator next to the
     * Microphone field label so the candidate knows which device to fix.
     */
    microphoneWarning?: boolean;
}

export default function SettingsModal({
    isOpen,
    onClose,
    onSave,
    initialSelectedCamera,
    initialSelectedMicrophone,
    initialSelectedSpeaker,
    colorScheme = 'default',
    hideCamera = false,
    microphoneWarning = false,
}: SettingsModalProps) {
    // Color scheme configuration
    const isBlackScheme = colorScheme === 'black'; const colorClasses = isBlackScheme ? {
        dialogBg: '',
        dialogText: '',
        dialogBorder: '',
        labelText: 'text-gray-900',
        iconColor: 'text-black',
        primaryButton: 'bg-black hover:bg-gray-800 text-white',
        secondaryButton: 'border-black text-black hover:bg-black hover:text-white'
    } : {
        dialogBg: '',
        dialogText: '',
        dialogBorder: '',
        labelText: '',
        iconColor: '',
        primaryButton: '',
        secondaryButton: ''
    };

    const [isLoading, setLoading] = useState<boolean>(false);
    const [cameras, setCameras] = useState<DeviceOption[]>([]);
    const [microphones, setMicrophones] = useState<DeviceOption[]>([]);
    const [speakers, setSpeakers] = useState<DeviceOption[]>([]);

    // Initialize state with props or default to empty string
    const [selectedCamera, setSelectedCamera] = useState<string>(initialSelectedCamera || "");
    const [selectedMicrophone, setSelectedMicrophone] = useState<string>(initialSelectedMicrophone || "");
    const [selectedSpeaker, setSelectedSpeaker] = useState<string>(initialSelectedSpeaker || "");

    // Effect to update internal state if initial props change while modal is open
    useEffect(() => {
        if (initialSelectedCamera !== selectedCamera) {
            setSelectedCamera(initialSelectedCamera || "");
        }
        if (initialSelectedMicrophone !== selectedMicrophone) {
            setSelectedMicrophone(initialSelectedMicrophone || "");
        }
        if (initialSelectedSpeaker !== selectedSpeaker) {
            setSelectedSpeaker(initialSelectedSpeaker || "");
        }
    }, [initialSelectedCamera, initialSelectedMicrophone, initialSelectedSpeaker]);

    useEffect(() => {
        // Only fetch devices when the modal is open
        if (isOpen) {
            setLoading(true);
            const getDevices = async () => {
                // Try to acquire a stream so labels are populated. This call
                // can legitimately fail during a live session (the mic is
                // already held by the voice SDK), so we intentionally catch
                // and continue — enumerateDevices still works and returns
                // devices (potentially without labels, but with device IDs).
                try {
                    await navigator.mediaDevices.getUserMedia({
                        video: !hideCamera,
                        audio: true,
                    });
                } catch {
                    // Permission already granted by active session — proceed
                    // to enumerate regardless.
                }

                try {
                    // Get all media devices
                    const devices = await navigator.mediaDevices.enumerateDevices();

                    const cameraOptions: DeviceOption[] = devices
                        .filter((device) => device.kind === "videoinput")
                        .map((device) => {
                            const originalLabel = device.label || `Camera (${device.deviceId.slice(0, 8)})`;
                            return {
                                deviceId: device.deviceId,
                                label: truncateLabel(originalLabel),
                                fullLabel: originalLabel
                            };
                        });

                    const microphoneOptions: DeviceOption[] = devices
                        .filter((device) => device.kind === "audioinput")
                        .map((device) => {
                            const originalLabel = device.label || `Microphone (${device.deviceId.slice(0, 8)})`;
                            return {
                                deviceId: device.deviceId,
                                label: truncateLabel(originalLabel),
                                fullLabel: originalLabel
                            };
                        });

                    const speakerOptions: DeviceOption[] = devices
                        .filter((device) => device.kind === "audiooutput")
                        .map((device) => {
                            const originalLabel = device.label || `Speaker (${device.deviceId.slice(0, 8)})`;
                            return {
                                deviceId: device.deviceId,
                                label: truncateLabel(originalLabel),
                                fullLabel: originalLabel
                            };
                        });

                    setCameras(cameraOptions);
                    setMicrophones(microphoneOptions);
                    setSpeakers(speakerOptions); // Add default option

                    // Validate current selection or pick a default if current is invalid/not set
                    // This uses the state (which should be synced with props by the other effect)
                    if (cameraOptions.length > 0) {
                        if (!selectedCamera || !cameraOptions.some(c => c.deviceId === selectedCamera)) {
                            setSelectedCamera(cameraOptions[0].deviceId);
                        }
                    } else {
                        setSelectedCamera(""); // No cameras available
                    }

                    if (microphoneOptions.length > 0) {
                        if (!selectedMicrophone || !microphoneOptions.some(m => m.deviceId === selectedMicrophone)) {
                            setSelectedMicrophone(microphoneOptions[0].deviceId);
                        }
                    } else {
                        setSelectedMicrophone("");
                    }

                    if (speakerOptions.length > 0) {
                        if (!selectedSpeaker || !speakerOptions.some(s => s.deviceId === selectedSpeaker)) {
                            setSelectedSpeaker(speakerOptions[0].deviceId);
                        }
                    } else {
                        setSelectedSpeaker("");
                    }
                } catch (error) {
                    console.error("Error enumerating media devices:", error);
                }
            };

            getDevices().finally(() => {
                setLoading(false);
            });
        }
    }, [isOpen]); // Re-run only when isOpen changes. Prop sync is handled by the other useEffect.

    const handleSave = () => {
        onSave({ // Call the new onSave prop
            camera: selectedCamera,
            microphone: selectedMicrophone,
            speaker: selectedSpeaker,
        });
        // onClose(); // Parent (VideoContainer) will call its own close handler after processing save.
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={`sm:max-w-md ${colorClasses.dialogBg}`}>
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">
                        Device Settings
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {!hideCamera && <div className="space-y-2">
                        <label className={`text-base font-medium flex items-center gap-2 ${colorClasses.labelText}`}>
                            <CameraIcon className={`h-5 w-5 ${colorClasses.iconColor}`} />
                            Camera
                        </label>
                        <Select value={selectedCamera} onValueChange={setSelectedCamera} disabled={isLoading}>
                            <SelectTrigger className="w-full">
                                {isLoading ? (
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                        <Loader2Icon className="h-4 w-4 animate-spin" />
                                        Loading devices...
                                    </span>
                                ) : (
                                    <SelectValue placeholder="Select camera" />
                                )}
                            </SelectTrigger>
                            <SelectContent>
                                {cameras.map((camera, index) => (
                                    <SelectItem
                                        key={index}
                                        value={camera.deviceId}
                                        title={camera.fullLabel}
                                    >
                                        {camera.label}
                                    </SelectItem>
                                ))}
                                {cameras.length === 0 && (
                                    <SelectItem value="no-camera" disabled>
                                        No cameras available
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>}

                    <div className="space-y-2">
                        <label className={`text-base font-medium flex items-center gap-2 ${colorClasses.labelText}`}>
                            <MicIcon className={`h-5 w-5 ${colorClasses.iconColor}`} />
                            Microphone
                            {microphoneWarning && (
                                <span
                                    aria-label="Microphone audio issue"
                                    title="Audio issue detected on this microphone"
                                    className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-yellow-400"
                                >
                                    <span className="text-[10px] font-bold text-gray-900 leading-none">!</span>
                                </span>
                            )}
                        </label>
                        <Select
                            value={selectedMicrophone}
                            onValueChange={setSelectedMicrophone}
                            disabled={isLoading}
                        >
                            <SelectTrigger className="w-full">
                                {isLoading ? (
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                        <Loader2Icon className="h-4 w-4 animate-spin" />
                                        Loading devices...
                                    </span>
                                ) : (
                                    <SelectValue placeholder="Select microphone" />
                                )}
                            </SelectTrigger>
                            <SelectContent>
                                {microphones.map((mic, index) => (
                                    <SelectItem
                                        key={index}
                                        value={mic.deviceId}
                                        title={mic.fullLabel}
                                    >
                                        {mic.label}
                                    </SelectItem>
                                ))}
                                {microphones.length === 0 && (
                                    <SelectItem value="no-mic" disabled>
                                        No microphones available
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <label className={`text-base font-medium flex items-center gap-2 ${colorClasses.labelText}`}>
                            <SpeakerIcon className={`h-5 w-5 ${colorClasses.iconColor}`} />
                            Speaker
                        </label>
                        <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker} disabled={isLoading}>
                            <SelectTrigger className="w-full">
                                {isLoading ? (
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                        <Loader2Icon className="h-4 w-4 animate-spin" />
                                        Loading devices...
                                    </span>
                                ) : (
                                    <SelectValue placeholder="Select speaker" />
                                )}
                            </SelectTrigger>
                            <SelectContent>
                                {speakers.map((speaker, index) => (
                                    <SelectItem
                                        key={index}
                                        value={speaker.deviceId}
                                        title={speaker.fullLabel}
                                    >
                                        {speaker.label}
                                    </SelectItem>
                                ))}
                                {speakers.length === 0 && (
                                    <SelectItem value="no-speaker" disabled>
                                        No speakers available
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter className="gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        className={`w-full sm:w-auto ${colorClasses.secondaryButton}`}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={isLoading}
                        className={`w-full sm:w-auto ${colorClasses.primaryButton}`}
                    >
                        Apply Settings
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}